// import { useGeocodingCore } from "@mapbox/search-js-react";
import { useEffect, useState, useCallback, useRef } from "react";
import { ArkCombobox } from "@ttc3k/trekker";
import { LngLatLike } from "mapbox-gl";
import { Position } from "geojson";
import { MapRef, MarkerDragEvent } from "react-map-gl";

export const defaultCoordinatesCA: Position = [-79.38355, 43.647938];

export type GoogleMapsAddressDetails = {
  placeId?: string;
  fullAddress: string;
  line1: string;
  line2: string;
  city: string;
  subdivision: string;
  postalCode: string;
  country: string;
  location: { type: string; coordinates: Position };
};

const isValidLngLat = (lng: unknown, lat: unknown) => {
  const parsedLng = Number(lng);
  const parsedLat = Number(lat);
  return (
    Number.isFinite(parsedLng) &&
    Number.isFinite(parsedLat) &&
    parsedLng >= -180 &&
    parsedLng <= 180 &&
    parsedLat >= -90 &&
    parsedLat <= 90
  );
};

/**
 * Hook to handle geocoding and address autofill
 * - This uses the Google Places API for suggestions and the MapBox api for drawing the map
 * @param input - The input value to search for
 * @param defaultCoordinates - The default coordinates to use
 * @param onAddressSelect - The function to call when an address is selected
 * @param onMarkerDrag - The function to call when a marker is dragged
 * @returns An object with the geocoding and address autofill state and handlers
 */
export const useGeocoding = (
  input: string,
  defaultCoordinates?: LngLatLike,
  onAddressSelect?: (foundSuggestion: GoogleMapsAddressDetails) => void,
  onMarkerDrag?: (e: MarkerDragEvent) => void,
  /**
   * The full address text of the currently-selected/resolved value, if any.
   * When `input` matches this exactly, we skip re-querying autocomplete
   * suggestions. Re-searching for an address that's already been fully
   * resolved is not just wasted work — because our autocomplete requests
   * are location-biased toward Canada, re-querying the formatted text of a
   * (non-biased) US address can fail to surface the original place, which
   * would otherwise desync the input's displayed value from the address
   * that was actually selected.
   */
  resolvedInputValue?: string,
) => {
  const defaultLng =
    defaultCoordinates &&
    "lng" in defaultCoordinates &&
    Number.isFinite(Number(defaultCoordinates.lng))
      ? Number(defaultCoordinates.lng)
      : 0;
  const defaultLat =
    defaultCoordinates &&
    "lat" in defaultCoordinates &&
    Number.isFinite(Number(defaultCoordinates.lat))
      ? Number(defaultCoordinates.lat)
      : 0;

  // Debounce the input to prevent excessive API calls
  const [debouncedInput, setDebouncedInput] = useState(input);

  // Update debouncedInput after a delay
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedInput(input);
    }, 250); // 250ms debounce delay

    return () => clearTimeout(handler); // cancel previous timeout if input changes
  }, [input]);

  /**
   * Boolean to check if `defaultCoordinates` is valid
   * Coordinates are valid if they exist and do not equal
   * the form's default values [0, 0]
   * - Note, when removing the value from the input field, the coordinate value
   *   is initially set to NaN, so we need to check for that too
   */
  const isDefaultCoordinatesValid =
    defaultCoordinates &&
    "lng" in defaultCoordinates &&
    "lat" in defaultCoordinates &&
    !(defaultLng === 0 && defaultLat === 0) &&
    isValidLngLat(defaultLng, defaultLat);

  // Map ref to bind to `Map` component
  const mapRef = useRef<MapRef | null>(null);

  /**
   *  Marker state for Marker component inside `Map`
   *  If `defaultCoordinates` are valid, this will default
   *  to coordinates that were already selected in the inputs
   *  Otherwise, set to [0, 0]
   */
  const [marker, setMarker] = useState<{ lng: number; lat: number }>({
    lng: isDefaultCoordinatesValid ? defaultLng : 0,
    lat: isDefaultCoordinatesValid ? defaultLat : 0,
  });
  // Ensure that when you select an operator or organization from the combobox,
  // that the map is centered on the operator or organization's coordinates
  useEffect(() => {
    if (isDefaultCoordinatesValid) {
      setMarker({
        lng: defaultLng,
        lat: defaultLat,
      });
      mapRef.current?.flyTo({
        center: { lng: defaultLng, lat: defaultLat },
        duration: 500,
      });
    }
  }, [isDefaultCoordinatesValid, defaultLng, defaultLat]);

  // Boolean to check if Marker is placed on the map
  const isMarkerPlaced = !(marker.lng === 0 && marker.lat === 0);

  // Loading state for the backend Google Places proxy suggestions call
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  // "Cleaned" suggestions as mapped values to pass into the `trekker` Combobox
  const [suggestionsMappedToCombobox, setSuggestionsMappedToCombobox] =
    useState<{ value: string; label: string }[]>([]);

  const placesApiUrl = `${import.meta.env.VITE_API_SERVER_URL}/api/google-places`;

  /**
   * Handler that gets called on input change
   * Call the backend Google Places proxy to retrieve suggestions
   * Set `suggestions`, `suggestionsMappedToCombobox` and `suggestionsLoading`
   * Default to empty array when input is empty
   */
  const handleInputChange = useCallback(async () => {
    if (!debouncedInput || debouncedInput.length === 0) {
      setSuggestionsMappedToCombobox([]);
      return;
    }

    if (resolvedInputValue && debouncedInput === resolvedInputValue) {
      setSuggestionsMappedToCombobox([]);
      return;
    }

    setSuggestionsLoading(true);

    try {
      const response = await fetch(`${placesApiUrl}/autocomplete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ input: debouncedInput }),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch address suggestions");
      }

      const data = (await response.json()) as {
        suggestions?: { value: string; label: string }[];
      };

      setSuggestionsMappedToCombobox(data.suggestions ?? []);
    } catch (error) {
      console.error("error", error);
      setSuggestionsMappedToCombobox([]);
    }
    setSuggestionsLoading(false);
  }, [debouncedInput, placesApiUrl, resolvedInputValue]);

  /**
   * Handler that gets called within `handleSelectAddress`
   * Fly to selected suggestion's coordinates on the Mapbox Map
   * Also move the Map marker to the suggestion's coordinates
   * @param coordinates
   */
  const handleFlyToAddress = (coordinates: Position) => {
    if (!isValidLngLat(coordinates[0], coordinates[1])) return;

    mapRef.current?.flyTo({
      center: coordinates as LngLatLike,
      duration: 2500,
    });
    setMarker({ lng: coordinates[0], lat: coordinates[1] });
  };

  /**
   * Handler that gets called when a suggestion is selected
   * From the Combobox's selected option, map the found address back to our `suggestions`
   * Extract data from `foundSuggestion` and set our form values to the suggestion
   * Call `handleFlyToAddress` to move map to proper coordinates on map
   * @param e
   */
  const handleSelectAddress = async (e: ArkCombobox.ValueChangeDetails) => {
    const placeId = e.value[0];
    if (!placeId) return;

    try {
      const response = await fetch(`${placesApiUrl}/details`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ placeId }),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch address details");
      }

      const placeDetails = (await response.json()) as GoogleMapsAddressDetails;

      onAddressSelect?.(placeDetails);
      const coordinates = placeDetails.location?.coordinates;
      handleFlyToAddress([
        coordinates?.[0] ?? defaultCoordinatesCA[0],
        coordinates?.[1] ?? defaultCoordinatesCA[1],
      ]);
    } catch (error) {
      console.error("error", error);
    }
  };

  /**
   * Handler that gets called on Map's Marker drag end
   * Sets the Marker to the new coordinates and will run optional `onMarkerDrag` function
   */
  const handleMarkerDragEnd = (e: MarkerDragEvent) => {
    setMarker({
      lng: e.lngLat.lng ?? 0,
      lat: e.lngLat.lat ?? 0,
    });
    onMarkerDrag?.(e);
  };

  /**
   * Function to get Map coordinates that are set on the Mapbox Map
   * If coordinates are valid, they will set to the `marker` `lng` and `lat`
   * otherwise, go back to default view
   */
  const getMapCoordinates = () => {
    if (!isDefaultCoordinatesValid || !isValidLngLat(marker.lng, marker.lat)) {
      return {
        longitude: defaultCoordinatesCA[0],
        latitude: defaultCoordinatesCA[1],
      };
    } else {
      return {
        longitude: marker.lng ?? 0,
        latitude: marker.lat ?? 0,
      };
    }
  };

  /**
   * useEffect to set `suggestionsLoading` to false when call to
   * backend Google Places proxy to fetch suggestions is complete
   */
  useEffect(() => {
    handleInputChange().then(() => setSuggestionsLoading(false));
  }, [handleInputChange]);

  return {
    suggestionsLoading,
    suggestionsMappedToCombobox,
    mapRef,
    marker,
    isMarkerPlaced,
    handleSelectAddress,
    handleMarkerDragEnd,
    handleFlyToAddress,
    getMapCoordinates,
    setMarker,
  };
};
